// @flow
/* eslint-disable no-nested-ternary */
import core from '@bravissimolabs/website-core';
import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages, type IntlShape } from 'react-intl';

import Bandeau from 'brastrap/common/bandeau/Bandeau';
import type { Bandeau as BandeauType } from 'brastrap/common/bandeau/bandeau.types';
import Chat from 'brastrap/containers/chat/ChatContainer';
import Container from 'brastrap/common/container/Container';
import SectionNav from 'brastrap/common/section-nav/SectionNav';
import Accordion from 'brastrap/common/accordion/Accordion';
import MarkDown from 'brastrap/editorial/markdown/Markdown';

import Meta from '../../../containers/MetaContainer';
import { setHelp } from '../../../actions/help';
import generateFaqSchema from '../faqSchemaGenerator/faqSchemaGenerator';

const messages = defineMessages({
  pageTitle: {
    id: 'help-section.title',
    defaultMessage: '{name} FAQs',
  },
  bandeauTitle: {
    id: 'help-landing.title',
    defaultMessage: 'Help & FAQs',
  },
  bandeauReturnNav: {
    id: 'help-section.return-nav',
    defaultMessage: 'All Topics',
  },
  description: {
    id: 'help-section.description',
    defaultMessage:
      "We're here to help with any questions about {topic}." +
      ' Have a look through our FAQs here, or give us a call.',
  },
});

const topicDescriptions = {
  shopping: 'shopping at Bravissimo',
  delivery: 'your delivery',
  payment: 'payments',
  'fitting & sizing advice': 'bra fitting and our clothing sizes',
  'technical assistance': 'our website or placing your order',
  'your questions answered ': 'anything',
  'your bra fitting questions answered': 'bra fittings',
  'your clothing questions answered': 'our clothing sizes',
};

export type LinkType = {
  label: string,
  url: Url,
  current?: boolean,
};

type TopicResult = {
  faqs: FaqListing[],
  name: string,
  topicLink: Url,
  pageTitle?: string,
  pageDescription?: string,
  isCentreAligned: boolean,
  isRightAligned: boolean,
};

type Props = {
  intl: IntlShape,
  bandeau: BandeauType,
  sectionNavLinks: LinkType[],
  topicResult: TopicResult[],
  pathname: string,
  hash: ?string,
  basePath: string,
};

export const HelpSectionPage = (props: Props) => {
  const currentChild = `${props.pathname
    .replace(props.basePath, '/')
    .replace(/\/$/, '')}${props.hash || ''}`;
  const faqSchema = generateFaqSchema(props.topicResult[0].faqs);
  const {
    pageTitle,
    name,
    faqs,
    isRightAligned,
    isCentreAligned,
  } = props.topicResult[0];
  const textAlignmentCss = isRightAligned
    ? 'right'
    : isCentreAligned
    ? 'center'
    : 'left';

  return (
    <Meta
      title={
        pageTitle ||
        props.intl.formatMessage(messages.pageTitle, {
          name: name,
        })
      }
      description={
        props.topicResult[0].pageDescription ||
        props.intl.formatMessage(messages.description, {
          topic: topicDescriptions[name.toLowerCase()] || name.toLowerCase(),
        })
      }
      schemas={[faqSchema]}
    >
      <Bandeau
        {...props.bandeau}
        title={props.intl.formatMessage(messages.bandeauTitle)}
        returnNav={{
          label: props.intl.formatMessage(messages.bandeauReturnNav),
          url: '/help/',
          displayBackTo: false,
        }}
      >
        <Chat />
      </Bandeau>
      <Container>
        <div className="l-grid">
          <div className="l-grid__unit l-grid__unit--nav">
            <SectionNav items={props.sectionNavLinks} />
          </div>
          <div className="l-grid__unit l-grid__unit--content">
            <div className="s-prose">
              <h2
                style={{
                  fontFamily: 'Editors Note',
                  fontSize: '1.5rem',
                  color: '#46433F',
                  textAlign: textAlignmentCss,
                  fontWeight: 100,
                }}
              >
                {name}
              </h2>
              <Accordion activeChild={currentChild}>
                {faqs.map(faq => (
                  <Accordion.Item
                    id={faq.faqLink}
                    href={faq.url}
                    label={faq.question}
                    key={faq.id}
                  >
                    <MarkDown content={faq.answer} />
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </Container>
    </Meta>
  );
};

HelpSectionPage.populateStore = async (store, props) => {
  const helpApi = core.help.help;
  const topic = props.params.topic;
  try {
    const help = await helpApi.getFaqByTopic(topic);
    return store.dispatch(setHelp(help));
  } catch (error) {
    return error;
  }
};

const mapStateToProps = state => ({
  sectionNavLinks: state.help.data.sectionNav,
  topicResult: state.help.data.topicLinks,
  pathname: state.app.routing.pathname,
  hash: state.app.routing.hash,
  basePath: state.app.config.basePath,
});

export default connect(mapStateToProps)(injectIntl(HelpSectionPage));
